// AGC: MachinesList - List view for all farm machines
// AGC: Visual Refresh Design System - matches Tasks/Lots/Weather pages
// AGC: Includes green gradient header, filters, and empty state

import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useMachinePermissions } from '../../hooks/useRBAC'
import { getMachines, createMachine } from '../../services/machinesService'
import type { MachineInstance, MachineStatus } from '../../types/machineInstance'
import { MachineCard } from './MachineCard'
import { PageHeader } from '../shared/PageHeader'
import { BottomNav, EmptyState } from '../shared'
import { Plus, Tractor, Loader2, Filter, RefreshCw } from 'lucide-react'
import { usePullToRefresh } from '../../hooks/usePullToRefresh'
import toast from 'react-hot-toast'

interface MachinesListProps {
  onBack: () => void
  onNavigate: (screen: string, params?: any) => void
}

/**
 * AGC: MachinesList - Main machines list page
 * AGC: Features:
 * AGC: - Grid of machine cards (matches lot list style)
 * AGC: - Filter by status (available/in_use/maintenance)
 * AGC: - Add new machine button (permission-gated)
 * AGC: - Empty state for new users
 * AGC: - Loading and error states
 */
export const MachinesList: React.FC<MachinesListProps> = ({ onBack, onNavigate }) => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()
  const { canCreate } = useMachinePermissions()

  const [machines, setMachines] = useState<MachineInstance[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<MachineStatus | 'all'>('all')

  // AGC: Load machines on mount and when component becomes visible
  useEffect(() => {
    loadMachines()
    
    // AGC: Reload when page becomes visible (user navigates back)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadMachines()
      }
    }
    
    // AGC: Listen for refresh-machines event (triggered by voice commands)
    const handleRefreshMachines = () => {
      console.log('🔄 AGC: Received refresh-machines event, reloading...')
      loadMachines()
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('refresh-machines', handleRefreshMachines)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('refresh-machines', handleRefreshMachines)
    }
  }, [])

  const loadMachines = async () => {
    try {
      setIsLoading(true)
      const token = await getAuthToken()
      const data = await getMachines(token)
      setMachines(data.filter((m) => !m.isArchived))
    } catch (error) {
      console.error('AGC: Error loading machines:', error)
      toast.error(t('machines.errors.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const { scrollRef, pullDistance, isRefreshing, pullThreshold, touchHandlers } = usePullToRefresh(
    async () => { await loadMachines() }
  )

  // AGC: Sort machines by createdAt descending (newest first) and filter by status
  const filteredMachines = useMemo(() => {
    const sorted = [...machines].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return bTime - aTime
    })
    if (filterStatus === 'all') return sorted
    return sorted.filter((m) => m.status === filterStatus)
  }, [machines, filterStatus])

  // AGC: Handle machine click
  const handleMachineClick = (machine: MachineInstance) => {
    onNavigate('machine-detail', { machineId: machine.id })
  }

  // AGC: Handle add new machine
  const handleAddMachine = () => {
    onNavigate('machine-form')
  }

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen app-bg">
        <PageHeader
          icon={Tractor}
          title={t('machines.title') || 'Maquinário'}
        />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  return (
    <div ref={scrollRef} className="min-h-screen app-bg pb-24 transition-colors overflow-y-auto" {...touchHandlers}>
      {/* AGC: Green gradient header matching Tasks/Lots */}
      <PageHeader
        icon={Tractor}
        title={t('machines.title') || 'Maquinário'}
        actions={
          <>
            <button
              onClick={loadMachines}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={t('common.refresh') || 'Atualizar'}
            >
              <RefreshCw className={`w-5 h-5 text-white ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            {canCreate && (
              <button
                onClick={handleAddMachine}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title={t('machines.addMachine') || 'Adicionar'}
              >
                <Plus className="w-5 h-5 text-white" />
              </button>
            )}
          </>
        }
      />

      {/* AGC: Pull-to-refresh indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          className="flex items-center justify-center gap-2 overflow-hidden transition-all duration-200 app-bg"
          style={{ height: isRefreshing ? pullThreshold : pullDistance }}
        >
          <RefreshCw className={`w-4 h-4 text-green-600 dark:text-green-400 transition-transform ${isRefreshing ? 'animate-spin' : ''}`} />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {isRefreshing ? (t('common.syncing') || 'Sincronizando...') : pullDistance >= pullThreshold ? (t('common.releaseToRefresh') || 'Solte para atualizar') : (t('common.pullToRefresh') || 'Puxe para atualizar')}
          </span>
        </div>
      )}

      <div className="px-4 py-4 space-y-4">
        {/* AGC: Summary line */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {filteredMachines.length} {filteredMachines.length === 1 ? (t('machines.machine') || 'máquina') : (t('machines.machines') || 'máquinas')}
          </p>
        </div>

        {/* AGC: Status Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filterStatus === 'all'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            {t('common.all') || 'Todas'} ({machines.length})
          </button>
          <button
            onClick={() => setFilterStatus('available')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filterStatus === 'available'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            {t('machines.statusAvailable') || 'Disponível'} ({machines.filter((m) => m.status === 'available').length})
          </button>
          <button
            onClick={() => setFilterStatus('in_use')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filterStatus === 'in_use'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            {t('machines.statusInUse') || 'Em Uso'} ({machines.filter((m) => m.status === 'in_use').length})
          </button>
          <button
            onClick={() => setFilterStatus('maintenance')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filterStatus === 'maintenance'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            {t('machines.statusMaintenance') || 'Manutenção'} ({machines.filter((m) => m.status === 'maintenance').length})
          </button>
        </div>

        {/* AGC: Machines Grid */}
        {filteredMachines.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMachines.map((machine) => (
              <MachineCard
                key={machine.id}
                machine={machine}
                onClick={() => handleMachineClick(machine)}
              />
            ))}
          </div>
        ) : (
          // AGC: Empty State
          <EmptyState
            icon={<Tractor />}
            title={filterStatus === 'all' ? (t('machines.noMachines') || 'Nenhuma máquina cadastrada') : (t('machines.noMachinesFiltered') || 'Nenhuma máquina neste status')}
            description={filterStatus === 'all' ? (t('machines.addFirstMachine') || 'Cadastre sua primeira máquina') : undefined}
            action={canCreate && filterStatus === 'all' ? {
              label: t('machines.addMachine') || 'Adicionar Máquina',
              onClick: handleAddMachine
            } : undefined}
          />
        )}
      </div>
      
      <BottomNav 
        active="machines" 
        onNavigate={(tab) => {
          if (tab === 'dashboard') onNavigate('dashboard')
          else if (tab === 'fields') onNavigate('lotes-list')
          else if (tab === 'tasks') onNavigate('tasks')
        }} 
      />
    </div>
  )
}

export default MachinesList
